// filepath: apps/web/src/features/cabinet/RequiredUserParameters/types.ts
//
// FR-UI-CABINET-3 — TypeScript wire-types that mirror the Go server
// contract at internal/userprofile/{store,handlers,required_parameters_config}.go.
//
// Drift between this file and the Go struct is a CONTRACT VIOLATION
// per the established pattern at apps/web/src/definitions/types.ts.

/**
 * One field in the required-parameter response. Mirrors
 * internal/userprofile.RequiredParameter.
 */
export interface RequiredParameter {
  /** Stable field ID (form definition key). */
  key: string;
  /** Locale-varying label. Server may pre-resolve via ?locale= or
   *  return the raw LocaleMap; the SPA re-resolves client-side when
   *  the user changes the active locale (FR-I18N-1 contract). */
  label: Record<string, string>;
  /** FR-FORM-1 field type ("string" | "number" | "enum" | etc.). */
  field_type: string;
  /** Always true for items in this slice (the slice is the
   *  `required: true` subset of the form definition). */
  required: true;
  /** User-supplied value. `null` when the user has not supplied a
   *  value (== `missing` when required). */
  value: unknown;
  /** Convenience flag: `required && (value === null)`. Drives the
   *  chrome-button "missing count" badge. */
  missing: boolean;
}

/**
 * Response shape for GET /api/me/required-parameters. Mirrors
 * internal/userprofile.RequiredParameterResponse.
 */
export interface RequiredParametersResponse {
  form_id: string;
  form_version: number;
  /** Server-side row_version (FR-ENT-8 / INV-7 optimistic concurrency). */
  row_version: number;
  /** True when every `required: true` field has a non-null value. */
  complete: boolean;
  fields: RequiredParameter[];
}

/**
 * Request body for PATCH /api/me/required-parameters.
 */
export interface PatchRequiredParametersRequest {
  row_version: number;
  values: Record<string, unknown>;
}

/**
 * 5-state classification (FR-UI-CORE-10) for the Required User
 * Parameters page. Mirrors the ObservabilityDashboardPage.DashboardStatus
 * enum.
 */
export type RequiredParametersStatus =
  | "loading"
  | "ready"
  | "empty"
  | "fetch-failure"
  | "permission-denied"
  | "stale-version"
  | "rate-limit";

/** Classify a TanStack Query error into the 5-state contract. */
export function classifyRequiredParametersError(err: {
  status?: number;
  code?: string;
}): Exclude<RequiredParametersStatus, "loading" | "ready" | "empty"> {
  if (err.status === 403) return "permission-denied";
  if (err.status === 404 || err.status === 410) return "stale-version";
  if (err.status === 409) return "stale-version";
  if (err.status === 429 || err.code === "rate_limited") return "rate-limit";
  return "fetch-failure";
}
